import { memo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { GlassButton } from '@/components/ui/GlassButton'
import { Card, CardContent } from '@/components/ui/card'
import {
  Sparkles,
  MapPin,
  Phone,
  Mail,
  Star,
  Eye,
  Clock,
  Truck,
  Shield,
  Users,
} from 'lucide-react'

const Hero = memo(() => {
  const navigate = useNavigate()

  const handleKIGeneratorClick = useCallback(() => {
    navigate('/ki-generator')
  }, [navigate])

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-background via-background to-muted/20 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-brand-purple/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-brand-pink/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-brand-blue/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6 pt-16 pb-12">
        {/* Hero Content */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left Column - Text & CTA */}
          <div className="space-y-6 text-center lg:text-left">
            {/* Regional Badge */}
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-brand-purple/10 to-brand-pink/10 border border-brand-purple/20 rounded-full">
              <MapPin className="h-4 w-4 text-brand-purple" />
              <span className="text-sm font-medium text-brand-purple">
                Regional hergestellt am Niederrhein
              </span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-3xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-brand-purple via-brand-pink to-brand-blue bg-clip-text text-transparent">
                  Individuelle Textillien, Geschenk- & Dekorationsartikel
                </span>
                <br />
                <span className="text-foreground">aus Tönisvorst</span>
              </h1>
              <p className="text-lg lg:text-xl xl:text-2xl text-muted-foreground leading-relaxed">
                Personalisierte Produkte für jeden Anlass - von Geburtstagen bis
                Hochzeiten
              </p>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 lg:gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-brand-purple" />
                <span>1000+ zufriedene Kunden</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-brand-pink" />
                <span>24h Support</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-yellow-500" />
                <span>4.9/5 Bewertungen</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <GlassButton
                variant="primary"
                size="lg"
                onClick={handleKIGeneratorClick}
                className="px-8 py-4 h-14"
              >
                <Sparkles className="h-4 w-4 mr-3" />
                Jetzt Motiv erstellen
              </GlassButton>
              <GlassButton
                variant="secondary"
                size="lg"
                className="px-8 py-4 h-14"
              >
                <Eye className="h-4 w-4 mr-3" />
                Shop durchstöbern
              </GlassButton>
            </div>

            {/* Contact Info */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 lg:gap-8 text-sm">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-brand-purple" />
                <span>02151-4798709</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-brand-purple" />
                <span>support@wunschparadies.de</span>
              </div>
            </div>
          </div>

          {/* Right Column - Personalization Card */}
          <div className="space-y-6">
            <Card className="bg-background/80 backdrop-blur-xl border-brand-purple/20 shadow-2xl">
              <CardContent className="p-8 lg:p-10 text-center space-y-6">
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-brand-purple/10 to-brand-pink/10 border border-brand-purple/20 rounded-full">
                  <Sparkles className="h-5 w-5 text-brand-purple" />
                  <span className="text-base font-medium text-brand-purple">
                    Personalisiert
                  </span>
                </div>
                <h3 className="text-2xl lg:text-3xl font-semibold">
                  Individuelle Produkte
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Erstelle dein eigenes personalisiertes Motiv mit unserem
                  KI-Generator oder lass dich von unserem Team beraten.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <GlassButton
                    variant="primary"
                    size="lg"
                    onClick={handleKIGeneratorClick}
                    className="px-6 py-3 h-12"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    KI-Generator
                  </GlassButton>
                  <GlassButton
                    variant="secondary"
                    size="lg"
                    className="px-6 py-3 h-12"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Beratung
                  </GlassButton>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Social Proof Section */}
        <div className="mt-16 lg:mt-20 text-center space-y-8 lg:space-y-10">
          <div className="space-y-4 lg:space-y-6">
            <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold">
              Vertrauen unserer Kunden
            </h2>
            <p className="text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Über 1000+ personalisierte Produkte erfolgreich hergestellt • 2-5
              Tage Lieferung • 100% Unikatigkeitsgarantie
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            <div className="flex items-center justify-center gap-3 lg:gap-4 p-6 lg:p-8 bg-background/50 rounded-xl border border-border/50">
              <div className="flex items-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="h-4 w-4 lg:h-5 lg:w-5 fill-yellow-400 text-yellow-400"
                  />
                ))}
              </div>
              <span className="text-sm lg:text-base font-medium">
                4.9/5 aus 500+ Bewertungen
              </span>
            </div>
            <div className="flex items-center justify-center gap-3 lg:gap-4 p-6 lg:p-8 bg-background/50 rounded-xl border border-border/50">
              <Truck className="h-5 w-5 lg:h-6 lg:w-6 text-brand-purple" />
              <span className="text-sm lg:text-base font-medium">
                2-5 Tage Lieferung
              </span>
            </div>
            <div className="flex items-center justify-center gap-3 lg:gap-4 p-6 lg:p-8 bg-background/50 rounded-xl border border-border/50">
              <Shield className="h-5 w-5 lg:h-6 lg:w-6 text-brand-purple" />
              <span className="text-sm lg:text-base font-medium">
                100% Zufriedenheitsgarantie
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
})

Hero.displayName = 'Hero'

export default Hero
